import React, { useMemo } from 'react';
import { GeoJSON } from 'react-leaflet';
import L from 'leaflet';
import { PerCrownNativeFeatureProps, PerCrownNativeWindow } from '../../lib/types';
import { shortTons, metersToFeet } from '../../lib/units';

// Local alias for the typed feature shape — the hook leaves features as
// `unknown[]` so the @types/geojson generic doesn't have to thread through
// the rest of the codebase. Cast at the boundary here, where we know the
// precompute schema is the only producer of this payload.
type CrownFeature = GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon, PerCrownNativeFeatureProps>;

interface PerCrownNativeOverlayProps {
  active: boolean;
  window: PerCrownNativeWindow | null;
}

// HTML-escape user/data-controlled strings before interpolating into the
// tooltip template. The body is built as an HTML string (Leaflet's
// `bindTooltip` accepts an HTML string the same way `bindPopup` does), so we
// lose React's auto-escaping. S3 is the only producer today, but a malformed
// or tampered GeoJSON should not be able to inject script via fields like
// `forest_type_name`. Same boundary applies whether we surface the data via
// hover (current) or click (legacy) — keep the escape unconditional.
const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const fmtNumber = (v: number, dp: number = 1): string =>
  Number.isFinite(v) ? v.toFixed(dp) : '—';

const fmtTons = (kg: number): string => {
  const t = shortTons(kg / 1000); // kg → Mg → short tons
  if (t >= 1) return `${t.toFixed(2)} t`;
  return `${(t * 2000).toFixed(0)} lb`;
};

// Build the popup HTML for a single crown feature. Inline styles cover the
// dark figma chrome inside the popup body; Leaflet's wrapper / tail keep their
// default white. Looks acceptable — the dark inner card reads as a
// deliberate "data tooltip" against the lighter speech-bubble shell.
// Note: the per-crown `fit_level === 5` (spatial-fallback) caveat is
// intentionally NOT surfaced in this hover tooltip. The audience for the
// SFI demo is non-technical, and "fit_level = 5" reads as jargon that
// doesn't pay for the cognitive load it imposes. The caveat still lives
// in PerCrownContent's left-panel AboutLayer copy for users who open
// the layer documentation, and fit_level=5 crowns are still excluded
// from the headline tree count — both rules unchanged.
const buildPopupHtml = (props: Partial<PerCrownNativeFeatureProps> | null | undefined): string => {
  const id = escapeHtml(String(props?.id ?? '?'));
  const forestType = escapeHtml(String(props?.forest_type_name ?? 'Unknown forest type'));
  const diaClass = escapeHtml(String(props?.dia_class ?? '—').replace(/_/g, ' '));
  const hM = Number(props?.h_m ?? 0);
  const hFt = metersToFeet(hM);
  const dbhCm = Number(props?.dbh_cm ?? 0);
  const agbKg = Number(props?.agb_kg ?? 0);
  const rootKg = Number(props?.root_kg ?? 0);
  const agbOnlyKg = Math.max(agbKg - rootKg, 0);

  return `
    <div style="font-family:'Roboto',sans-serif;min-width:220px;color:#FBFFEF;background:#0E1117;padding:8px 10px;border-radius:6px;border:1px solid rgba(193,240,79,0.3);">
      <div style="font-family:'Roboto Mono',monospace;font-size:10px;color:#9CA3AF;text-transform:uppercase;letter-spacing:0.05em;">Tree #${id}</div>
      <div style="margin-top:2px;font-size:13px;color:#c1f04f;">${forestType} · ${diaClass}</div>
      <div style="margin-top:8px;padding-top:6px;border-top:1px solid #374151;display:grid;grid-template-columns:auto 1fr;column-gap:14px;row-gap:3px;font-size:12px;">
        <span style="color:#9CA3AF;">Height</span>
        <span style="font-family:'Roboto Mono',monospace;text-align:right;">${fmtNumber(hM)} m · ${fmtNumber(hFt, 0)} ft</span>
        <span style="color:#9CA3AF;">DBH (est)</span>
        <span style="font-family:'Roboto Mono',monospace;text-align:right;">${fmtNumber(dbhCm)} cm</span>
        <span style="color:#9CA3AF;">Total biomass</span>
        <span style="font-family:'Roboto Mono',monospace;color:#c1f04f;text-align:right;">${fmtTons(agbKg)}</span>
        <span style="color:#9CA3AF;">AGB-only</span>
        <span style="font-family:'Roboto Mono',monospace;text-align:right;">${fmtTons(agbOnlyKg)}</span>
      </div>
    </div>
  `;
};

/**
 * Renders the native-resolution per-crown polygons inside the demo window.
 * Mounts a per-layer canvas renderer so the dense geometry (~25 k features)
 * stays performant; the existing supply-shed boundary keeps the map's
 * default SVG renderer.
 *
 * Visibility is controlled entirely by `active` — DetailView wires that to
 * `nativeViewActive && activeLayers.includes('tree_crown_segmentation')`.
 * No zoom listener; the user opts in via the left-panel button.
 */
export const PerCrownNativeOverlay: React.FC<PerCrownNativeOverlayProps> = ({
  active,
  window,
}) => {
  // L.canvas() instance is stable per mount — using useMemo with no deps is
  // intentional. A new canvas would force the GeoJSON to remount on every
  // render, which is expensive at 22 k features.
  const canvasRenderer = useMemo(() => L.canvas({ padding: 0.5 }), []);

  // Build a stable identity for the GeoJSON's `key`. The crownCount + first
  // feature id is enough to tell two windows apart without serialising the
  // whole FeatureCollection. Remounting on data change resets Leaflet's
  // internal caches cleanly.
  const layerKey = useMemo(() => {
    if (!window) return 'empty';
    const first = window.features[0] as CrownFeature | undefined;
    const firstId = first?.properties?.id ?? 'na';
    return `native-${window.crownCount}-${firstId}`;
  }, [window]);

  if (!active || !window) return null;

  const styleFn = (feature?: GeoJSON.Feature) => {
    const props = (feature?.properties ?? {}) as Partial<PerCrownNativeFeatureProps>;
    const tier = Math.max(1, Math.min(5, Number(props.tier ?? 3)));
    return {
      fillColor: window.binColors[tier - 1] ?? '#78C679',
      fillOpacity: 0.85,
      weight: 0.5,
      color: '#000',
      opacity: 0.4,
    };
  };

  const onEachFeature = (feature: GeoJSON.Feature, layer: L.Layer) => {
    const props = (feature.properties ?? {}) as Partial<PerCrownNativeFeatureProps>;
    const html = buildPopupHtml(props);
    // Hover tooltip — desktop pointer affordance. `sticky: true` makes
    // the tooltip follow the cursor across the polygon's surface; without
    // it Leaflet anchors to the feature centroid, which on a sprawling
    // crown polygon can sit far from the cursor. Dark figma chrome on
    // the wrapper comes from `.leaflet-tooltip.per-crown-tooltip` in
    // src/styles/globals.css.
    layer.bindTooltip(html, {
      sticky: true,
      direction: 'top',
      offset: [0, -8],
      opacity: 0.98,
      className: 'per-crown-tooltip',
    });
    // Click/tap popup — required for touch devices where hover doesn't
    // fire. Same HTML body as the tooltip; Leaflet's popup chrome is
    // already dark-themed via `.leaflet-popup-content-wrapper` rules in
    // globals.css. `autoPan: true` (Leaflet default) keeps the popup in
    // view if the tap lands near the viewport edge — matters most on
    // narrow phones. Same trigger handles desktop clicks as a bonus.
    layer.bindPopup(html, {
      autoPan: true,
      closeButton: true,
      maxWidth: 260,
      className: 'per-crown-popup',
    });
  };

  // react-leaflet's GeoJSONProps doesn't expose `renderer` at the top-level
  // even though Leaflet's underlying L.geoJSON accepts it via its options
  // bag. Forwarding via a cast keeps the rest of the call site type-checked
  // while the canvas renderer still reaches Leaflet — this is the only
  // dense vector layer in the map; using SVG here would visibly stutter at
  // ~22k features.
  const extraProps: { renderer: L.Renderer } = { renderer: canvasRenderer };

  // Context boundary (Yosemite NP for the Sierra Nevada demo). One thin
  // yellow line with no interaction — purpose is purely "frame the demo
  // in a place the audience instantly recognises". Rendered separately
  // from the crown polygons so it stays SVG (single low-vertex polygon,
  // looks crisper than a canvas stroke) while crowns stay on canvas.
  // Mounted as a sibling so they share the overlay's lifecycle.
  const contextBoundaryStyle = {
    color: '#c1f04f',
    weight: 2.5,
    opacity: 0.9,
    fill: false,
    dashArray: undefined,
  } as const;

  return (
    <>
      <GeoJSON
        key={layerKey}
        data={{ type: 'FeatureCollection', features: window.features } as GeoJSON.FeatureCollection}
        style={styleFn as never}
        onEachFeature={onEachFeature}
        {...(extraProps as object)}
      />
      {window.contextBoundary && (
        <GeoJSON
          key={`${layerKey}-context`}
          data={{
            type: 'Feature',
            properties: { name: window.contextBoundary.name },
            geometry: window.contextBoundary.geometry,
          } as GeoJSON.Feature}
          style={contextBoundaryStyle as never}
          interactive={false}
        />
      )}
    </>
  );
};
